import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';

/**
 * Small building blocks shared by every card block.
 *
 * `CopyableValue` exists because of section 23.3: a user checking a counterparty
 * copies an identifier, an address or a name straight into a contract or an
 * invoice, and hand-retyping a 14-digit CNPJ is how mistakes are made. It also
 * renders an em dash for an absent value, so a missing field never looks like an
 * empty string that might have been loaded incorrectly.
 */

export function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-[var(--color-muted-fg)]">{label}</dt>
      <dd className="mt-0.5 text-sm break-words">{children}</dd>
    </div>
  );
}

export function FieldGrid({ children }: { children: ReactNode }): ReactNode {
  return <dl className="grid gap-3 sm:grid-cols-2">{children}</dl>;
}

export function CopyableValue({
  value,
  mono = false,
}: {
  value: string | null | undefined;
  mono?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (value === null || value === undefined || value === '') {
    return <span className="text-[var(--color-muted-fg)]">—</span>;
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be denied; silently doing nothing is better than
      // an error toast for a convenience action.
    }
  };

  return (
    <span className="group inline-flex items-center gap-1.5">
      <span className={mono ? 'font-mono text-[13px]' : undefined}>{value}</span>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={t('lookup.copyField', { field: value })}
        title={copied ? t('common.copied') : t('common.copy')}
        className="opacity-0 transition group-hover:opacity-100 focus:opacity-100 print:hidden"
      >
        {copied ? (
          <Check aria-hidden className="h-3.5 w-3.5 text-risk-low" />
        ) : (
          <Copy aria-hidden className="h-3.5 w-3.5 text-[var(--color-muted-fg)]" />
        )}
      </button>
    </span>
  );
}

/** Render a translated label with a sane fallback for keys not yet translated. */
export function useBlockLabel(key: string): string {
  const { t } = useTranslation();
  return t(`blocks.${key}.label`, key);
}

/**
 * The active BCP-47 tag.
 *
 * Every date and money value on the card goes through `Intl` with this tag
 * (section 10.2). Reacting to the i18next language rather than to
 * `navigator.language` is what makes a switch in the header re-render a card
 * that is already on screen: `useTranslation` subscribes to `languageChanged`,
 * so the component re-renders and the numbers are reformatted.
 */
export function useLocale(): string {
  const { i18n } = useTranslation();
  return i18n.language;
}
